// Containment for the local-dev game-dev runner (D-085 pivot).
//
// The local-dev runner executes `claude -p` on the operator's dev machine to
// author/modify a game in a per-job scratch dir. That invocation has the full
// Claude Code toolset, so the runner wraps it in a sandbox launcher. This module
// is the PURE argv-construction half — given a sandbox choice + paths, it returns
// the `{cmd, args}` to spawn. No execution, no filesystem, no env reads here, so
// it is fully unit-testable (the IO half lives in the runner package).
//
// Design constraints baked in:
//   - `claude` needs network (to reach the Anthropic API) and read access to its
//     auth dir (~/.claude). So sandboxes keep the network and whitelist/bind the
//     claude home read-path — they only confine the *filesystem write surface* to
//     the job's scratch dir. They are NOT a network jail.
//   - `none` is a deliberate escape hatch (the runner logs a loud warning); the
//     operator picks a real sandbox in their environment.

import { z } from 'zod';

/** Which local sandbox launcher wraps the per-job `claude -p` invocation. */
export const RunnerSandbox = z.enum(['none', 'firejail', 'bwrap']);
export type RunnerSandbox = z.infer<typeof RunnerSandbox>;

export interface SandboxOptions {
  /** Which launcher to use. */
  readonly sandbox: RunnerSandbox;
  /** The writable per-job scratch dir the job runs in (absolute path). */
  readonly scratchDir: string;
  /**
   * Home dir holding Claude Code auth (`~/.claude`), which the sandboxed process
   * needs read access to. Omit only if auth lives elsewhere / `none`.
   */
  readonly claudeHome?: string;
}

/**
 * Wrap a base command (e.g. `claude` + its args) in a sandbox launcher, returning
 * the `{cmd, args}` to spawn. Pure — constructs argv only.
 *
 * - `none`     → run the base command unchanged (escape hatch; caller should warn).
 * - `firejail` → confine the write surface to the scratch dir (+ claude home),
 *                keep the network (claude needs the API). Lightweight, common on Linux.
 * - `bwrap`    → bubblewrap: read-only bind the whole FS, read-write bind only the
 *                scratch dir (+ claude home), chdir into the scratch dir.
 */
export function buildSandboxCommand(
  baseCmd: string,
  baseArgs: readonly string[],
  opts: SandboxOptions,
): { cmd: string; args: string[] } {
  switch (opts.sandbox) {
    case 'none':
      return { cmd: baseCmd, args: [...baseArgs] };

    case 'firejail': {
      const args = [
        '--quiet',
        '--private-tmp',
        `--whitelist=${opts.scratchDir}`,
        ...(opts.claudeHome ? [`--whitelist=${opts.claudeHome}`] : []),
        '--',
        baseCmd,
        ...baseArgs,
      ];
      return { cmd: 'firejail', args };
    }

    case 'bwrap': {
      const args = [
        '--ro-bind', '/', '/',
        '--bind', opts.scratchDir, opts.scratchDir,
        ...(opts.claudeHome ? ['--bind', opts.claudeHome, opts.claudeHome] : []),
        '--dev', '/dev',
        '--proc', '/proc',
        '--chdir', opts.scratchDir,
        '--',
        baseCmd,
        ...baseArgs,
      ];
      return { cmd: 'bwrap', args };
    }
  }
}
